import moment from 'moment';

export type IndianKycDocType = 'AADHAAR' | 'PAN' | 'DL';

export interface DocumentPayload {
    id: number | null;
    documentNo: string | null;
    imageFront: string | null;
    imageBack: string | null;
    dob: string | null;
    docDate: string | null;
    // keep all other fields, but we don't validate them here
    [key: string]: any;
}

export interface KycPayload {
    aadhar: DocumentPayload;
    panCard: DocumentPayload;
    drivingLicense: DocumentPayload;
}

export interface KycError {
    docType: IndianKycDocType;
    field:
        | 'documentNo'
        | 'imageFront'
        | 'imageBack'
        | 'dob'
        | 'docDate'
        | 'age';
    message: string;
}

export interface KycValidationResult {
    updatedPayload: KycPayload;
    errors: KycError[];
    changed: {
        aadhar: boolean;
        panCard: boolean;
        drivingLicense: boolean;
    };
    isValid: boolean;
}

/**
 * Helper: normalize document number (trim + uppercase)
 */
const normalizeDocNo = (docNo?: string | null): string => {
    return (docNo ?? '').trim().toUpperCase();
};
const normalizeDocDate = (docDate?: string | null): any => {
    return !!docDate ? moment(docDate).valueOf() : null;
};

/**
 * Helper: Aadhaar validation (India)
 *  - 12 digits
 *  - first digit 2–9
 */
const isValidAadhaar = (docNo: string): boolean => {
    const aadhaarRegex = /^[2-9][0-9]{11}$/;
    return aadhaarRegex.test(docNo);
};

/**
 * Helper: PAN validation (India)
 *  - Format: AAAAA9999A
 */
const isValidPan = (docNo: string): boolean => {
    return docNo?.length > 0;
    ///const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    //return panRegex.test(docNo);
};

/**
 * Helper: Driving Licence validation (India)
 * NOTE: India has multiple formats; this is a *generic* check.
 * You can tighten it if needed.
 */
const isValidDlNumber = (docNo: string): boolean => {
    // Very generic: at least 10 chars, alphanumeric
    const dlRegex = /^[A-Z0-9]{10,20}$/i;
    return dlRegex.test(docNo);
};

/**
 * Helper: check if age is >= minYears based on dob string (any moment-parsable format)
 */
const isAgeAtLeast = (dobStr: string, minYears: number): boolean => {
    const dob = moment(dobStr)?.isValid()
        ? moment(dobStr)
        : moment(dobStr, ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'], true);

    if (!dob.isValid()) return false;

    const years = moment().diff(dob, 'years');
    return years >= minYears;
};

/**
 * Helper: is date in future?
 */
const isFutureDate = (dateStr: string): boolean => {
    const d = moment(dateStr, moment.ISO_8601, true).isValid()
        ? moment(dateStr)
        : moment(dateStr, ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'], true);

    if (!d.isValid()) return false;
    return d.isAfter(moment(), 'day');
};

/**
 * Main validator
 * @param current current KYC payload (form state)
 * @param previous previous KYC payload (from server / initial state)
 */
export const validateKycPayload = (
    current: KycPayload,
    previous?: KycPayload,
): KycValidationResult => {
    const errors: KycError[] = [];

    // --- Normalize document numbers in a copy of payload ---
    const updatedPayload: KycPayload = {
        aadhar: {
            ...current.aadhar,
            documentNo: normalizeDocNo(current?.aadhar?.documentNo),
        },
        panCard: {
            ...current.panCard,
            documentNo: normalizeDocNo(current?.panCard?.documentNo),
        },
        drivingLicense: {
            ...current.drivingLicense,
            documentNo: normalizeDocNo(current?.drivingLicense?.documentNo),
            docDate: normalizeDocDate(current?.drivingLicense?.docDate),
        },
    };

    // --- Determine which documents have changed compared to previous ---
    const aadharChanged =
        !previous ||
        normalizeDocNo(previous?.aadhar?.documentNo) !==
            normalizeDocNo(current?.aadhar?.documentNo) ||
        (previous?.aadhar?.imageFront || '') !==
            (current?.aadhar?.imageFront || '') ||
        (previous?.aadhar?.imageBack || '') !==
            (current?.aadhar?.imageBack || '');

    const panChanged =
        !previous ||
        normalizeDocNo(previous?.panCard?.documentNo) !==
            normalizeDocNo(current?.panCard?.documentNo) ||
        (previous?.panCard?.image || '') !== (current?.panCard?.image || '');

    const dlChanged =
        !previous ||
        normalizeDocNo(previous?.drivingLicense?.documentNo) !==
            normalizeDocNo(current?.drivingLicense?.documentNo) ||
        (previous?.drivingLicense?.imageFront || '') !==
            (current?.drivingLicense?.imageFront || '') ||
        (previous?.drivingLicense?.imageBack || '') !==
            (current?.drivingLicense?.imageBack || '') ||
        (previous?.drivingLicense?.docDate || '') !==
            (current?.drivingLicense?.docDate || '');

    const changed = {
        aadhar: aadharChanged,
        panCard: panChanged,
        drivingLicense: dlChanged,
    };

    // ===================== AADHAAR VALIDATION =====================
    if (aadharChanged) {
        const { documentNo, imageFront, imageBack } = updatedPayload.aadhar;
        const aadhaarNo = documentNo ?? '';

        if (!aadhaarNo) {
            errors.push({
                docType: 'AADHAAR',
                field: 'documentNo',
                message: 'Please enter Aadhaar number.',
            });
        } else if (!isValidAadhaar(aadhaarNo)) {
            errors.push({
                docType: 'AADHAAR',
                field: 'documentNo',
                message: 'Please enter a valid 12-digit Aadhaar number.',
            });
        }

        if (!imageFront) {
            errors.push({
                docType: 'AADHAAR',
                field: 'imageFront',
                message: 'Please upload Aadhaar front image.',
            });
        }

        if (!imageBack) {
            errors.push({
                docType: 'AADHAAR',
                field: 'imageBack',
                message: 'Please upload Aadhaar back image.',
            });
        }
    }

    // ===================== PAN VALIDATION =====================
    if (panChanged) {
        const { documentNo, imageFront, image } = updatedPayload.panCard;
        const panNo = documentNo ?? '';

        if (!panNo) {
            errors.push({
                docType: 'PAN',
                field: 'documentNo',
                message: 'Please enter PAN number.',
            });
        } else if (!isValidPan(panNo)) {
            errors.push({
                docType: 'PAN',
                field: 'documentNo',
                message: 'Please enter a valid PAN number (e.g. AAAAA9999A).',
            });
        }

        if (!imageFront && !image) {
            errors.push({
                docType: 'PAN',
                field: 'imageFront',
                message: 'Please upload PAN image.',
            });
        }
    }

    // ===================== DRIVING LICENCE VALIDATION =====================
    if (dlChanged) {
        const { documentNo, imageFront, imageBack, dob, docDate } =
            updatedPayload.drivingLicense;
        const dlNo = documentNo ?? '';

        // DL number
        if (!dlNo) {
            errors.push({
                docType: 'DL',
                field: 'documentNo',
                message: 'Please enter Driving Licence number.',
            });
        } else if (!isValidDlNumber(dlNo)) {
            errors.push({
                docType: 'DL',
                field: 'documentNo',
                message:
                    'Please enter a valid Driving Licence number (alphanumeric, 10–20 characters).',
            });
        }

        // At least one image
        if (!imageFront && !imageBack) {
            errors.push({
                docType: 'DL',
                field: 'imageFront',
                message:
                    'Please upload at least one Driving Licence image (front or back).',
            });
        }

        // DOB must be >= 18 years
        if (!docDate) {
            errors.push({
                docType: 'DL',
                field: 'dob',
                message: 'Please select date of birth for Driving Licence.',
            });
        } else if (!isAgeAtLeast(docDate, 18)) {
            errors.push({
                docType: 'DL',
                field: 'age',
                message: 'Driver must be at least 18 years old.',
            });
        }

        // docDate: not mandatory, but if present, not future and age >=18 at docDate
        /* if (docDate) {
            if (isFutureDate(docDate)) {
                errors.push({
                    docType: 'DL',
                    field: 'docDate',
                    message:
                        'Licence issue/validity date cannot be in the future.',
                });
            }

            if (dob && isAgeAtLeast(dob, 18) === false) {
                errors.push({
                    docType: 'DL',
                    field: 'age',
                    message:
                        'Licence issue date is not valid for an age below 18 years. Please check DOB and licence date.',
                });
            }
        }*/
    }

    return {
        updatedPayload,
        errors,
        changed,
        isValid: errors.length === 0,
    };
};
